import React from 'react'

import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import { useShow } from '../../../../hooks/useShow'
import { useForm } from 'react-hook-form'
import { SubmitButton } from '../../../../components/SubmitButton/SubmitButton'
import { ICreateUser } from '../../../../api/types/user'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationCreateUser } from '../../../../api/mutations'
import { ErrorExplanation } from '../../../../components/ErrorExplanation/ErrorExplanation'
import { DialogActions, DialogContent, Modal } from '../../../../components/Modal/Modal'
import { FormRow } from '../../../../components/FormRow/FormRow'
import { Checkbox, FormControlLabel, TextField, Theme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { useApiErrors } from '../../../../hooks/useApiErrors'
import { usersQueryKey } from '../../../../api/queries'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      margin: theme.spacing(0.5),
    },
  })
)

export const CreateUser = () => {
  const { show, doHide, doShow } = useShow()
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const { register, handleSubmit, errors, getValues } = useForm<ICreateUser>()
  const queryClient = useQueryClient()
  const classes = useStyles()

  const { mutate: createUserMutation, isLoading: isMutating } = useMutation(
    (newUser: ICreateUser) => mutationCreateUser(newUser),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, 'Nie udało się stworzyć użytkownika')
      },
      onSuccess: async () => {
        setApiErrors([])
        queryClient.invalidateQueries(usersQueryKey()).then(doHide)
      },
    }
  )
  const onSubmit = (data: ICreateUser) => createUserMutation(data)

  return (
    <>
      <Button
        variant="outlined"
        className={classes.button}
        color="primary"
        size="small"
        startIcon={<AddIcon />}
        onClick={doShow}
      >
        New user
      </Button>

      <Modal open={show} onClose={doHide} title="Create new user">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <FormRow>
              <ErrorExplanation errors={apiErrors} />
            </FormRow>
            <FormRow>
              <TextField
                name="username"
                label="Username"
                fullWidth={true}
                variant="outlined"
                inputProps={{
                  ref: register({
                    required: 'Required',
                  }),
                }}
                error={!!errors.username}
                helperText={errors.username && errors.username.message}
              />
            </FormRow>

            <FormRow>
              <TextField
                type="password"
                name="password"
                label="Password"
                fullWidth={true}
                variant="outlined"
                inputProps={{
                  ref: register({
                    required: 'Required',
                    minLength: {
                      value: 8,
                      message: 'Must have at least 8 characters',
                    },
                  }),
                }}
                error={!!errors.password}
                helperText={errors.password && errors.password.message}
              />
            </FormRow>

            <FormRow>
              <TextField
                type="password"
                name="passwordConfirmation"
                label="Password confirmation"
                fullWidth={true}
                variant="outlined"
                inputProps={{
                  ref: register({
                    validate: value =>
                      value === getValues('password') || 'The passwords do not match',
                  }),
                }}
                error={!!errors.passwordConfirmation}
                helperText={errors.passwordConfirmation && errors.passwordConfirmation.message}
              />
            </FormRow>

            <FormRow>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="Admin"
                labelPlacement="end"
                name="admin"
                inputRef={register}
              />
            </FormRow>
            <FormRow>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="GM"
                labelPlacement="end"
                name="gm"
                inputRef={register}
              />
            </FormRow>
          </DialogContent>
          <DialogActions>
            <SubmitButton disabled={isMutating}>Create</SubmitButton>
          </DialogActions>
        </form>
      </Modal>
    </>
  )
}
